import React from 'react';
import { 
    Box, 
    CircularProgress, 
    Paper, 
    Typography, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Chip,
    Divider
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Search as SearchIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Send as SendIcon,
    Message as MessageIcon
} from '@mui/icons-material';
import { useChemoRecords } from '../hooks/chemo/useChemoRecords';
import { useUser } from '../contexts/UserContext';
import ChemoReportsList from '../components/ChemoRecords/ChemoReportsList';
import ChemoReportForm from '../components/ChemoRecords/ChemoReportForm';
import ChemoRecordsHeader from '../components/ChemoRecords/ChemoRecordsHeader';
import ChemoRecordsTable from '../components/ChemoRecords/ChemoRecordsTable';
import CustomSnackbar from '../components/CustomSnackbar';
import { styles } from '../styles/ChemoRecordsStyles';
import { chemoTranslations } from '../hooks/chemo/chemoTranslations';
import { FIELD_NAMES, SERVER_VALUE_YES, DASH_PLACEHOLDER, CHECK_MARK, TOP_RECORDS_OPTIONS, EMPTY_STRING } from '../hooks/chemo/chemoConstants';

const ChemoRecordsPage = () => {
    const { user } = useUser();

    const {
        // Data and loading states
        loading,
        searchTerm,
        showForm,
        currentReport,
        filteredReports,
        fetchedRecords,
        isLoadingRecords,
        error,
        topRecords,
        
        // UI state management
        snackbar,
        closeSnackbar,
        resultDialog,
        closeResultDialog,
        dateSearch,
        updateDateSearch,
        
        // Handlers
        handleSubmit,
        handleSaveReport,
        handleTopRecordsChange,
        handleDateSearch,
        fetchHospitalReport,
        handleCheckboxChange,
        handleFieldChange,
        formatDate,
        openNewReportForm,
        openExistingReport,
        closeForm,
        handleSearchChange,

        // Message dialog
        messageDialog,
        openMessageDialog,
        closeMessageDialog,
        updateMessageText,
        handleSendMessage
    } = useChemoRecords();

    const canCreate = user?.roles?.some(r =>
        r.ChemoRecordsPage && r?.properties[0]?.ChemoRecords?.Creator === true
    );

    const canView = user?.roles?.some(r =>
        r.ChemoRecordsPage && r?.properties[0]?.ChemoRecords?.Viewer === true
    );

    React.useEffect(() => {
        if (user?.hospital && !showForm) {
            fetchHospitalReport(user.hospital, topRecords);
        }
    }, [user?.hospital, topRecords, showForm, fetchHospitalReport]);

    return (
        <Box sx={styles.pageContainer}>
            {!showForm ? (
                <>
                    <ChemoRecordsHeader
                        searchTerm={searchTerm}
                        onSearchChange={handleSearchChange}
                        canCreate={canCreate}
                        onNewReport={openNewReportForm}
                        onOpenMessage={openMessageDialog}
                    />

                    {isLoadingRecords ? (
                        <Box sx={styles.loadingContainer}>
                            <CircularProgress />
                            <Typography variant="body1" sx={{ marginTop: 2 }}>
                                {chemoTranslations.form.loadingFromHospital}
                            </Typography>
                        </Box>
                    ) : fetchedRecords && fetchedRecords.length > 0 ? (
                        <Box sx={{ marginTop: 2 }}>
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'space-between', 
                                alignItems: { xs: 'stretch', sm: 'center' }, 
                                marginBottom: 2,
                                gap: { xs: 2, sm: 0 }
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {chemoTranslations.form.chemoRecordsPrefix}{user?.hospital || EMPTY_STRING}
                                </Typography>
                                <FormControl size="small" sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' } }}>
                                    <InputLabel id="top-records-label">{chemoTranslations.form.numRecordsLabel}</InputLabel>
                                    <Select
                                        labelId="top-records-label"
                                        value={topRecords || TOP_RECORDS_OPTIONS[1]}
                                        label={chemoTranslations.form.numRecordsLabel}
                                        onChange={(e) => handleTopRecordsChange(e.target.value, user?.hospital)}
                                    >
                                        {TOP_RECORDS_OPTIONS.map(option => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Paper sx={{ padding: { xs: 1.5, sm: 2 }, marginBottom: 2, backgroundColor: '#f9f9f9' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: 2 }}>
                                    {chemoTranslations.form.dateRangeSearch}
                                </Typography>
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 2, 
                                    alignItems: { xs: 'stretch', sm: 'center' }
                                }}>
                                    <TextField
                                        label={chemoTranslations.form.startDate}
                                        type="date"
                                        size="small"
                                        value={dateSearch.startDate}
                                        onChange={(e) => updateDateSearch('startDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        sx={{ minWidth: { xs: '100%', sm: 200 } }}
                                    />
                                    <TextField
                                        label={chemoTranslations.form.endDate}
                                        type="date"
                                        size="small"
                                        value={dateSearch.endDate}
                                        onChange={(e) => updateDateSearch('endDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        sx={{ minWidth: { xs: '100%', sm: 200 } }}
                                    />
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SearchIcon />}
                                        onClick={handleDateSearch}
                                        disabled={isLoadingRecords}
                                        fullWidth={true}
                                        sx={{ 
                                            display: { xs: 'flex', sm: 'inline-flex' },
                                            width: { xs: '100%', sm: 'auto' }
                                        }}
                                    >
                                        {chemoTranslations.form.search}
                                    </Button>
                                </Box>
                            </Paper>
                            
                            {fetchedRecords.map((report, reportIndex) => (
                                <Accordion key={reportIndex} sx={{ marginBottom: 2 }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ 
                                            backgroundColor: '#f5f5f5',
                                            '&:hover': { backgroundColor: '#e8e8e8' }
                                        }}
                                    >
                                        <Box sx={{ width: '100%' }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {chemoTranslations.form.hospital}
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                        {report[FIELD_NAMES.SPECVALUE] || DASH_PLACEHOLDER}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6} sm={6} md={2}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {chemoTranslations.form.startDate}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {report[FIELD_NAMES.FROMDATE] ? formatDate(report[FIELD_NAMES.FROMDATE]) : DASH_PLACEHOLDER}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6} sm={6} md={2}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {chemoTranslations.form.endDate}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {report[FIELD_NAMES.TODATE] ? formatDate(report[FIELD_NAMES.TODATE]) : DASH_PLACEHOLDER}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6} sm={6} md={2}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {chemoTranslations.labels.createdBy}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {report[FIELD_NAMES.SNAME] || DASH_PLACEHOLDER}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6} sm={6} md={2}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {chemoTranslations.labels.numRecords}
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                                        {report[FIELD_NAMES.SUBFORM]?.length || 0}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{ 
                                            overflow: 'auto',
                                            display: { xs: 'none', md: 'block' }
                                        }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: '#f9f9f9', borderBottom: '2px solid #ddd' }}>
                                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{chemoTranslations.table.row}</th>
                                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{chemoTranslations.table.deliveryDate}</th>
                                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{chemoTranslations.table.day}</th>
                                                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{chemoTranslations.table.description}</th>
                                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{chemoTranslations.table.customerNumber}</th>
                                                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{chemoTranslations.table.customerName}</th>
                                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{chemoTranslations.table.willArrive}</th>
                                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{chemoTranslations.table.willNotArrive}</th>
                                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{chemoTranslations.table.endTreatment}</th>
                                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{chemoTranslations.table.updateDeliveryDate}</th>
                                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{chemoTranslations.table.order}</th>
                                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{chemoTranslations.table.remainingDoses}</th>
                                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{chemoTranslations.table.loaded}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(report[FIELD_NAMES.SUBFORM] || []).map((record, index) => (
                                                        <tr
                                                            key={record[FIELD_NAMES.ORDI1] || index}
                                                            style={{
                                                                backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa',
                                                                borderBottom: '1px solid #eee'
                                                            }}
                                                        >
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>{record[FIELD_NAMES.ROW_ID]}</td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>{formatDate(record[FIELD_NAMES.DUEDATE])}</td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>{record[FIELD_NAMES.DAY]}</td>
                                                            <td style={{ padding: '8px', textAlign: 'right', maxWidth: '200px' }}>{record[FIELD_NAMES.CUSTDES]}</td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>{record[FIELD_NAMES.CUSTNAME]}</td>
                                                            <td style={{ padding: '8px', textAlign: 'right' }}>{record[FIELD_NAMES.CDES]}</td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                                {record[FIELD_NAMES.APPROVED] === SERVER_VALUE_YES ? CHECK_MARK : DASH_PLACEHOLDER}
                                                            </td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                                {record[FIELD_NAMES.NOT_APPROVED] === SERVER_VALUE_YES ? CHECK_MARK : DASH_PLACEHOLDER}
                                                            </td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                                {record[FIELD_NAMES.FINISH] === SERVER_VALUE_YES ? CHECK_MARK : DASH_PLACEHOLDER}
                                                            </td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                                {record[FIELD_NAMES.TO_DUE_DATE] || DASH_PLACEHOLDER}
                                                            </td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>{record[FIELD_NAMES.ORDNAME]}</td>
                                                            <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{record[FIELD_NAMES.ORDBAL]}</td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                                {record[FIELD_NAMES.LOADED] === SERVER_VALUE_YES ? CHECK_MARK : DASH_PLACEHOLDER}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Box>
                                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                            {(report[FIELD_NAMES.SUBFORM] || []).map((record, index) => (
                                                <Paper
                                                    key={record[FIELD_NAMES.ORDI1] || index}
                                                    sx={{
                                                        padding: 2,
                                                        marginBottom: 2,
                                                        backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa'
                                                    }}
                                                >
                                                    <Grid container spacing={1.5}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="textSecondary">{chemoTranslations.table.row}</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{record[FIELD_NAMES.ROW_ID]}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="textSecondary">{chemoTranslations.table.deliveryDate}</Typography>
                                                            <Typography variant="body2">{formatDate(record[FIELD_NAMES.DUEDATE])}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="textSecondary">{chemoTranslations.table.day}</Typography>
                                                            <Typography variant="body2">{record[FIELD_NAMES.DAY]}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="textSecondary">{chemoTranslations.table.customerNumber}</Typography>
                                                            <Typography variant="body2">{record[FIELD_NAMES.CUSTNAME]}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant="caption" color="textSecondary">{chemoTranslations.table.description}</Typography>
                                                            <Typography variant="body2">{record[FIELD_NAMES.CUSTDES]}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant="caption" color="textSecondary">{chemoTranslations.table.customerName}</Typography>
                                                            <Typography variant="body2">{record[FIELD_NAMES.CDES]}</Typography>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Typography variant="caption" color="textSecondary">{chemoTranslations.table.willArrive}</Typography>
                                                            <Typography variant="body2">{record[FIELD_NAMES.APPROVED] === SERVER_VALUE_YES ? CHECK_MARK : DASH_PLACEHOLDER}</Typography>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Typography variant="caption" color="textSecondary">{chemoTranslations.table.willNotArrive}</Typography>
                                                            <Typography variant="body2">{record[FIELD_NAMES.NOT_APPROVED] === SERVER_VALUE_YES ? CHECK_MARK : DASH_PLACEHOLDER}</Typography>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Typography variant="caption" color="textSecondary">{chemoTranslations.table.endTreatment}</Typography>
                                                            <Typography variant="body2">{record[FIELD_NAMES.FINISH] === SERVER_VALUE_YES ? CHECK_MARK : DASH_PLACEHOLDER}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="textSecondary">{chemoTranslations.table.order}</Typography>
                                                            <Typography variant="body2">{record[FIELD_NAMES.ORDNAME]}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="textSecondary">{chemoTranslations.table.remainingDoses}</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{record[FIELD_NAMES.ORDBAL]}</Typography>
                                                        </Grid>
                                                        {record[FIELD_NAMES.TO_DUE_DATE] && (
                                                            <Grid item xs={12}>
                                                                <Typography variant="caption" color="textSecondary">{chemoTranslations.table.updateDeliveryDate}</Typography>
                                                                <Typography variant="body2">{record[FIELD_NAMES.TO_DUE_DATE]}</Typography>
                                                            </Grid>
                                                        )}
                                                        <Grid item xs={12}>
                                                            <Typography variant="caption" color="textSecondary">{chemoTranslations.table.loaded}</Typography>
                                                            <Typography variant="body2">{record[FIELD_NAMES.LOADED] === SERVER_VALUE_YES ? CHECK_MARK : DASH_PLACEHOLDER}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Paper>
                                            ))}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    ) : loading ? (
                        <Box sx={styles.loadingContainer}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <ChemoReportsList
                            reports={filteredReports}
                            canView={canView}
                            onOpenReport={openExistingReport}
                        />
                    )}
                </>
            ) : (
                <ChemoReportForm
                    report={currentReport}
                    onSubmit={handleSubmit}
                    onCancel={closeForm}
                    hospital={user?.hospital || EMPTY_STRING}
                    fetchedRecords={fetchedRecords}
                    isLoadingRecords={isLoadingRecords}
                    error={error}
                    handleCheckboxChange={handleCheckboxChange}
                    handleFieldChange={handleFieldChange}
                    formatDate={formatDate}
                    onSave={handleSaveReport}
                />
            )}

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={closeSnackbar}
            />

            <Dialog
                open={resultDialog.open}
                onClose={closeResultDialog}
                maxWidth="md"
                fullWidth
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        margin: { xs: 2, sm: 3 },
                        width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)' }
                    }
                }}
            >
                <DialogTitle sx={{
                    backgroundColor: resultDialog.data?.success === false && resultDialog.data?.failedCount > 0
                        ? '#fff3e0' 
                        : '#e8f5e9',
                    color: '#37474f',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                    padding: { xs: 2, sm: 3 }
                }}>
                    {resultDialog.data?.success === false && resultDialog.data?.failedCount > 0 ? (
                        <>
                            <InfoIcon sx={{ color: '#ff9800', fontSize: { xs: 28, sm: 32 } }} />
                            <Typography variant="h6" sx={{ 
                                fontWeight: 600,
                                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                textAlign: { xs: 'center', sm: 'right' }
                            }}>
                                {chemoTranslations.dialog.saveWithErrors}
                            </Typography>
                        </>
                    ) : (
                        <>
                            <SuccessIcon sx={{ color: '#4caf50', fontSize: { xs: 28, sm: 32 } }} />
                            <Typography variant="h6" sx={{ 
                                fontWeight: 600,
                                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                textAlign: { xs: 'center', sm: 'right' }
                            }}>
                                {chemoTranslations.dialog.savedSuccessfully}
                            </Typography>
                        </>
                    )}
                </DialogTitle>
                <DialogContent sx={{ paddingTop: { xs: 2, sm: 3 }, padding: { xs: 2, sm: 3 } }}>
                    {resultDialog.data && (
                        <Box>

                            <Box sx={{ 
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2, 
                                marginBottom: 3,
                                justifyContent: 'center'
                            }}>
                                <Chip
                                    icon={<SuccessIcon />}
                                    label={`${resultDialog.data.updatedCount || 0} ${chemoTranslations.dialog.recordsUpdatedSuccess}`}
                                    color="success"
                                    sx={{ 
                                        fontSize: { xs: '0.875rem', sm: '1rem' }, 
                                        padding: { xs: '16px 8px', sm: '20px 10px' },
                                        width: { xs: '100%', sm: 'auto' }
                                    }}
                                />
                                {resultDialog.data.failedCount > 0 && (
                                    <Chip
                                        icon={<ErrorIcon />}
                                        label={`${resultDialog.data.failedCount} ${chemoTranslations.dialog.recordsFailed}`}
                                        color="error"
                                        sx={{ 
                                            fontSize: { xs: '0.875rem', sm: '1rem' }, 
                                            padding: { xs: '16px 8px', sm: '20px 10px' },
                                            width: { xs: '100%', sm: 'auto' }
                                        }}
                                    />
                                )}
                            </Box>

                            {resultDialog.data.results && resultDialog.data.results.length > 0 && (
                                <Box sx={{ marginBottom: 3 }}>
                                    <Typography variant="subtitle1" sx={{ 
                                        fontWeight: 600, 
                                        color: '#4caf50',
                                        marginBottom: 1,
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        gap: 1
                                    }}>
                                        <SuccessIcon /> {chemoTranslations.dialog.successfulRecords}
                                    </Typography>
                                    <List sx={{ 
                                        backgroundColor: '#f1f8f4',
                                        borderRadius: '8px',
                                        padding: '8px',
                                        maxHeight: { xs: '150px', sm: '200px' },
                                        overflowY: 'auto'
                                    }}>
                                        {resultDialog.data.results.map((item, index) => (
                                            <ListItem key={index} sx={{ padding: '4px 8px' }}>
                                                <ListItemText
                                                    primary={`${chemoTranslations.dialog.rowPrefix} ${item.rowId}: ${item.result?.[FIELD_NAMES.CUSTNAME] || item.result?.[FIELD_NAMES.CDES] || chemoTranslations.labels.unknownCreator}`}
                                                    secondary={item.result?.[FIELD_NAMES.CDES] || EMPTY_STRING}
                                                    primaryTypographyProps={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
                                                    secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}

                            {resultDialog.data.errors && resultDialog.data.errors.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle1" sx={{ 
                                        fontWeight: 600, 
                                        color: '#f44336',
                                        marginBottom: 1,
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        gap: 1
                                    }}>
                                        <ErrorIcon /> {chemoTranslations.dialog.failedRecords}
                                    </Typography>
                                    <List sx={{ 
                                        backgroundColor: '#ffebee',
                                        borderRadius: '8px',
                                        padding: '8px',
                                        maxHeight: { xs: '150px', sm: '200px' },
                                        overflowY: 'auto'
                                    }}>
                                        {resultDialog.data.errors.map((item, index) => (
                                            <Box key={index}>
                                                <ListItem sx={{ padding: '4px 8px', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                    <ListItemText
                                                        primary={`${chemoTranslations.dialog.rowPrefix} ${item.rowId}`}
                                                        secondary={
                                                            <>
                                                                <Typography component="span" variant="body2" color="error">
                                                                    {item.error}
                                                                </Typography>
                                                                {item.details?.data?.error?.message && (
                                                                    <Typography component="div" variant="caption" color="text.secondary" sx={{ marginTop: 0.5 }}>
                                                                        {item.details.data.error.message}
                                                                    </Typography>
                                                                )}
                                                            </>
                                                        }
                                                        primaryTypographyProps={{ fontSize: { xs: '0.85rem', sm: '0.95rem' }, fontWeight: 600 }}
                                                    />
                                                </ListItem>
                                                {index < resultDialog.data.errors.length - 1 && <Divider />}
                                            </Box>
                                        ))}
                                    </List>
                                </Box>
                            )}

                            {resultDialog.data.message && (
                                <Typography sx={{ 
                                    marginTop: 2, 
                                    padding: 2, 
                                    backgroundColor: '#fff3e0',
                                    borderRadius: '8px',
                                    fontSize: '0.95rem',
                                    color: '#e65100'
                                }}>
                                    {resultDialog.data.message}
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px' }}>
                    <Button
                        onClick={closeResultDialog}
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {chemoTranslations.dialog.close}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={messageDialog.open}
                onClose={closeMessageDialog}
                maxWidth="sm"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        margin: { xs: 2, sm: 3 },
                        width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)' },
                        borderRadius: { xs: '8px', md: '12px' }
                    }
                }}
            >
                <DialogTitle sx={{
                    backgroundColor: '#2c3e50',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    padding: { xs: 2, sm: 3 }
                }}>
                    <MessageIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                    <Typography variant="h6" sx={{
                        fontWeight: 600,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}>
                        {chemoTranslations.message.titlePrefix}{user?.hospital || EMPTY_STRING}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ padding: { xs: 2, sm: 3 }, paddingTop: { xs: '24px !important', sm: '24px !important' } }}>
                    <Typography variant="body2" sx={{ color: '#546e7a', marginBottom: 2 }}>
                        {chemoTranslations.message.writeMessage}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={8}
                        placeholder={chemoTranslations.message.placeholder}
                        value={messageDialog.message}
                        onChange={(e) => updateMessageText(e.target.value)}
                        disabled={messageDialog.sending}
                        inputProps={{
                            dir: 'rtl',
                            style: { textAlign: 'right' }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px'
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{
                    padding: { xs: '12px 16px', sm: '16px 24px' },
                    gap: 1
                }}>
                    <Button
                        onClick={closeMessageDialog}
                        variant="outlined"
                        disabled={messageDialog.sending}
                        sx={{
                            color: '#546e7a',
                            borderColor: '#cfd8dc',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                borderColor: '#90a4ae',
                                backgroundColor: '#f5f5f5'
                            }
                        }}
                    >
                        {chemoTranslations.labels.cancel}
                    </Button>
                    <Button
                        onClick={() => handleSendMessage(user?.hospital)}
                        variant="contained"
                        disabled={!messageDialog.message?.trim() || messageDialog.sending}
                        startIcon={messageDialog.sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                        sx={{
                            backgroundColor: '#3498db',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#2980b9'
                            },
                            '&:disabled': {
                                backgroundColor: '#cfd8dc'
                            }
                        }}
                    >
                        {messageDialog.sending ? chemoTranslations.message.sending : chemoTranslations.message.send}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ChemoRecordsPage;
